import { useCallback, useEffect, useRef, useState } from 'react';

/** Keep the same PDF editor mounted in native fullscreen and its viewport fallback. */
export function usePdfFullscreen(
  options: { boundarySelector?: string; escapeDisabled?: boolean } = {},
) {
  const { boundarySelector, escapeDisabled = false } = options;
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [active, setActive] = useState(false);
  const [pending, setPending] = useState(false);
  const [notice, setNotice] = useState('');
  const mounted = useRef(true);

  const target = useCallback(
    () =>
      (boundarySelector ? rootRef.current?.closest<HTMLElement>(boundarySelector) : null) ??
      rootRef.current,
    [boundarySelector],
  );

  useEffect(() => {
    mounted.current = true;
    const root = target();
    function changed() {
      if (document.fullscreenElement !== root) {
        setActive(false);
        setNotice('');
        buttonRef.current?.focus();
      }
    }
    document.addEventListener('fullscreenchange', changed);
    return () => {
      mounted.current = false;
      document.removeEventListener('fullscreenchange', changed);
      if (root && document.fullscreenElement === root) {
        void document.exitFullscreen?.().catch(() => undefined);
      }
    };
  }, [target]);

  useEffect(() => {
    if (!active) return;
    function escape(event: KeyboardEvent) {
      if (event.key !== 'Escape' || escapeDisabled) return;
      if (document.fullscreenElement === target()) {
        void document.exitFullscreen().catch(() => {
          if (mounted.current && document.fullscreenElement === target()) {
            setNotice('Use Exit fullscreen to return to the page. Your notes stay here.');
          }
        });
      } else if (!document.fullscreenElement) {
        setActive(false);
        setNotice('');
        buttonRef.current?.focus();
      }
    }
    document.addEventListener('keydown', escape);
    return () => document.removeEventListener('keydown', escape);
  }, [active, target, escapeDisabled]);

  async function toggle() {
    if (pending) return;
    const root = target();
    if (!root) return;
    setNotice('');
    if (active) {
      if (document.fullscreenElement === root) {
        setPending(true);
        try {
          await document.exitFullscreen();
        } catch {
          if (mounted.current) setNotice('Use Escape to leave fullscreen. Your notes stay here.');
          return;
        } finally {
          if (mounted.current) setPending(false);
        }
      }
      if (mounted.current) {
        setActive(false);
        buttonRef.current?.focus();
      }
      return;
    }
    setActive(true);
    if (!root.requestFullscreen) {
      setNotice('Expanded to fill this page. Your browser controls remain visible.');
      return;
    }
    setPending(true);
    try {
      await root.requestFullscreen();
    } catch {
      if (mounted.current)
        setNotice('Expanded to fill this page. Your browser controls remain visible.');
    } finally {
      if (mounted.current) setPending(false);
    }
  }

  return { rootRef, buttonRef, active, pending, notice, toggle };
}
